// import the html parser
import ReactHtmlParser from 'react-html-parser'

/**
 * order By
 *
 * Returns a sorted array of objects ordered by properties and orders.
 *
 * Returns a sorted array of objects ordered by properties and orders.
 * Uses `Array.prototype.sort()`, `Array.prototype.reduce()` on the props array
 * with a default value of 0, use array destructuring to swap the properties
 * position depending on the order passed. If no `orders` array is passed it
 * sort by `'asc'` by default.
 *
 * @param {Array} arr
 * @param {Array} props
 * @param {Array} orders
 * @return {Array} A sorted array of objects ordered by properties and orders.
 */
const orderBy = (arr, props, orders) =>
  [...arr].sort((a, b) =>
    props.reduce((acc, prop, i) => {
      if (acc === 0) {
        const [p1, p2] =
          orders && orders[i] === 'desc'
            ? [b[prop], a[prop]]
            : [a[prop], b[prop]]
        acc = p1 > p2 ? 1 : p1 < p2 ? -1 : 0
      }
      return acc
    }, 0)
  )

/**
 * Dynamic sort: Returns a function to sort array of objects
 * according to a property specified
 * @param {string} key that will be used to sort the objects
 * @returns (Function) function to sort array of objects
 */
export function dynamicSort(property) {
  var sortOrder = 1
  if (property[0] === '-') {
    sortOrder = -1
    property = property.substr(1)
  }
  return function (a, b) {
    /* next line works with strings and numbers,
     * and you may want to customize it to your needs
     */
    var result =
      a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0
    return result * sortOrder
  }
}

/**
 * checkLocale: returns the correct string to display based on locale
 * or the original value if pipe character is missing
 * @returns the correct string to display based on locale
 */
export function checkLocale(value, locale) {
  if (value === null || value === undefined) {
    return ''
  }
  if (value.indexOf('|') === -1) return value
  return locale === 'en-us' || locale === 'ja-jp'
    ? value.split('|')[0]
    : value.split('|')[1]
}

/**
 * mutateData: mutate the sortable component data to add the newest fields from contentful
 * @returns an array of objects that are merged together by key
 */
export const mutateData = (data, sortableData) => {
  if (!data && !sortableData) {
    return
  }
  const excludedFields = [
    'title',
    'type',
    'description',
    'logo',
    'headerConfiguration',
    'website',
    'link',
    'hqLocation',
  ]
  const newData = data.map(({ data, sys }) => {
    const newItem = {}
    Object.keys(data).forEach((key) => {
      if (!excludedFields.includes(key)) {
        newItem[key] = data[key]['en-US']
      }
    })
    newItem['id'] = sys ? sys.id : ''
    return newItem
  })
  return sortableData.map((t1) => {
    return {
      node: {
        ...t1.node,
        ...newData.find(({ id }) => {
          return id === t1.node.contentful_id
        }),
      },
    }
  })
}

/**
 * flatten: returns an array of ids
 * @returns {array}
 */
export function flatten(array) {
  const arrayOfTags = array.map((tag) => addTagSharedSlug(tag))
  return Object.entries(arrayOfTags).map(([key, value]) => {
    return value.sharedSlug
  })
}

/**
 * flattenTags: add key and value to array that is just the an array of tag ids
 * @returns {array}
 */
export function flattenTags(articles) {
  let updatedArticles = articles.map(({ node }) => {
    return {
      node: {
        ...node,
        t: flatten(node.tags.nodes),
      },
    }
  })
  return updatedArticles
}

/**
 * getRelated: returns an array of blog posts from wordpress that are the most recent
 * and have the most related tags
 * @returns {array}
 */
export function getRelated(articles, article) {
  const maxArticles = 3
  const identityMap = {}

  article['t'] = flatten(article.tags.nodes)

  let taggedPosts = articles
    .filter(({ node }) => node.slug !== article.slug)
    .filter(({ node }) => {
      return node.tags.nodes.length > 0
    })
    .map(({ node }) => {
      return {
        ...node,
        t: flatten(node.tags.nodes),
      }
    })

  let tags = article.t.length > 0 ? article.t : []

  if (!!tags === false || tags.length === 0) {
    return taggedPosts.splice(0, maxArticles).map((post) => {
      return {
        node: {
          ...post,
        },
      }
    })
  }

  const getSlug = (article) => {
    return article.slug
  }

  const addToMap = (article) => {
    const slug = getSlug(article)
    if (!identityMap.hasOwnProperty(slug)) {
      identityMap[slug] = {
        node: article,
        points: 0,
      }
    }
  }

  const addTagsPoints = (article, tags) => {
    const tagPoint = 1
    const slug = getSlug(article)

    article.t.forEach((aTag) => {
      if (tags.includes(aTag)) {
        identityMap[slug].points += tagPoint
      }
    })
  }

  const getIdentityMapAsArray = () => {
    return Object.keys(identityMap).map((slug) => identityMap[slug])
  }

  for (let taggedPost of taggedPosts) {
    addToMap(taggedPost)
    addTagsPoints(taggedPost, tags)
  }

  const arrayIdentityMap = getIdentityMapAsArray()
  const similarArticles = orderBy(arrayIdentityMap, ['points'], ['desc'])

  return similarArticles.splice(0, maxArticles)
}

/**
 * getLocaleData: if locale is another language
 * check for that translation if no translation
 * exists return the default language
 * @returns {array}
 */
export function getLocaleData(data, location) {
  // if no location return data
  if (!location) {
    return data
  }

  if (!data) {
    return null
  }

  // If  no data return null
  if (!data && typeof data.translated === 'undefined') {
    return null
  }

  if (location.pathname.indexOf('zh-cn') !== -1) {
    if (data.translated['zh_CN'] !== undefined) {
      return data.translated['zh_CN']
    } else {
      return data
    }
  } else {
    return data
  }
}

/**
 * addTagSharedSlug: this will add the English slug to all translations
 * @returns {object}
 */
export function addTagSharedSlug(data) {
  // If  no data return null
  if (!data || typeof data.slug === 'undefined') {
    return null
  }

  const { slug } = data

  // Add Shared slug to main translation
  data['sharedSlug'] = slug

  return {
    ...data,
  }
}

/**
 * addKey: this will update the key
 * @returns {object}
 */
function addKey(data) {
  // If  no data return null
  if (!data && typeof data.translated === 'undefined') {
    return null
  }

  for (let i = 0; i < data.translated.length; i++) {
    if (data.translated[i]) {
      const locale = data.translated[i].locale.locale
      data.translated[locale] = data.translated[i]
      delete data.translated[i]
    }
  }
  return {
    ...data,
  }
}

/**
 * mapLocaleData: this will update the key
 * of translation to the nation's abbreviated
 * country code.
 * @returns {object}
 */
export function mapLocaleData(data, type) {
  // If  no data return null
  if (!data) {
    return null
  }
  if (type === 'post') {
    return addKey(data)
  } else if (type === 'tag') {
    return addKey(addTagSharedSlug(data))
  } else if (type === 'author') {
    const object = data.userProfile.authorProfile
    if (!object) {
      return null
    }
    return addKey(object)
  } else if (type === 'node') {
    return data.map((node) => {
      return addKey(node)
    })
  } else {
    return data.map(({ node }) => {
      return {
        node: addKey(node),
      }
    })
  }
}

/**
 * defaultNode: A default node
 * that can used as placeholder
 * data for a wordpress post
 * @returns {object}
 */
export const defaultNode = {
  node: {
    id: '',
    isSticky: null,
    title: '',
    locale: {
      locale: 'en_US',
    },
    date: '',
    content: '',
    status: 'publish',
    slug: '',
    excerpt: '',
    featuredImage: null,
    tags: {
      nodes: [],
    },
    author: {
      node: {
        firstName: null,
        lastName: null,
        description: null,
        name: '',
        slug: '',
        extraCapabilities: null,
        userProfile: {
          authorProfile: null,
        },
      },
    },
    postOptions: {
      featuredOverlayAlignment: 'left',
      metaDescription: null,
      metaTitle: '',
    },
    translated: [null],
    t: [],
  },
}

/**
 * featuredBlogData: this method
 * return both an array of posts
 * and the featured post for the
 * home page
 * @returns {object}
 */
export function featuredBlogData(data) {
  // If  no data return null
  if (!data) {
    return null
  }
  // Check for posts marked isSticky
  let fPost = data.filter(({ node }) => node.isSticky === true)

  // Loop over isSticky posts and sort by date newest to oldest
  fPost = fPost.sort((a, b) => {
    let dateA = new Date(a.node.date)
    let dateB = new Date(b.node.date)
    return dateB - dateA
  })

  // Check if there are isSticky posts and select the first one
  // as the featured post
  const featuredPost = fPost.length > 0 ? fPost[0] : data[0]

  // Check if there are isSticky posts and select the first one
  // to filter out of the posts that are displayed
  // so there is not a doubling of the featured posts in the rest
  // of the posts
  const mutatedData =
    fPost.length > 0
      ? data.filter(({ node }) => fPost[0].node.id !== node.id)
      : data.slice(1)

  return {
    featuredPost,
    mutatedData,
  }
}

/**
 * decodeHtml: return a html node value
 * to decode any html entities that are
 * coming from wordpress.
 * @returns {string}
 */
export const decodeHtml = (html) => {
  var txt = document.createElement('textarea')
  txt.innerHTML = html
  return txt.value
}

/**
 * isSvgOrGif(url): pass image url with file extension
 * as an argument then returns true if file extension
 * includes gif or svg.
 * @param {String} url
 * @returns {boolean}
 */
export const isSvgOrGif = (url) => {
  if (!url) {
    return false
  }
  if (url.indexOf('.gif') !== -1 || url.indexOf('.svg') !== -1) {
    return true
  } else {
    return false
  }
}

/**
 * Get the user IP and Location
 * @return location or undefined
 */
export const getLocation = async () => {
  try {
    const ipRes = await fetch('https://api.ipify.org/?format=json')
    const ipJson = await ipRes.json()
    const locationRes = await fetch('https://ipapi.co/' + ipJson.ip + '/json/')
    const locationJson = await locationRes.json()
    return locationJson
  } catch (error) {
    console.error(error)
    return
  }
}

export const preventOrphan = (string, locale) => {
  // a little safeguarding: simply return if the
  // data passed isn't suitable
  if (!string || string.length < 0) {
    return string
  }

  // trim whitespace and then split into an array
  // of words
  const words = string.trim().split('')

  // count the number of words
  const totalWords = words.length

  // if there are fewer than four words then don't do anything -
  // otherwise we will end up with an orphan on the first
  // line instead (!)
  if (totalWords < 4) {
    return string
  }

  // wrap the last two words in a nobr
  const formattedLastWords = `<nobr>${words[totalWords - 2]}${
    words[totalWords - 1]
  }</nobr>`

  // remove the last two words from the array
  words.splice(-2, 2)

  // return the remaining words, re-joined with a space, followed
  // by the last two words (wrapped in a nobr), as a JSX object
  return ReactHtmlParser(`${words.join('')} ${formattedLastWords}`)
}
